import React, { useEffect, useMemo, useState } from 'react';
import { Search, ChevronUp, ChevronDown, Download, X } from 'lucide-react';
import { EcointelLottieMark } from '../EcoIntelLoader';

/**
 * Shared admin list primitive. Handles toolbar search, filter chips, column
 * sorting, client-side pagination, optional row selection and CSV export.
 *
 * Designed to be a thin shell so admin sections supply their own row markup
 * via the `renderRow` prop. Filtering/sorting happens here so every admin
 * list scales the same way as the user/property tables grow past 100 rows.
 *
 * Column shape:
 *   { key, label, sortable?: boolean, sortValue?: (row) => any,
 *     align?: 'left'|'right', className?: string }
 *
 * Filter chip group shape:
 *   { id, label, options: [{ value, label, count?: number }],
 *     selected: string[], onChange: (next: string[]) => void,
 *     multi?: boolean }
 */
const PAGE_SIZE_OPTIONS = [25, 50, 100, 'all'];

function defaultSortValue(row, key) {
  const v = row?.[key];
  if (v == null) return '';
  if (typeof v === 'number' || typeof v === 'boolean') return v;
  return String(v).toLowerCase();
}

function compareValues(a, b) {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true });
}

function escapeCsvCell(value) {
  if (value == null) return '';
  const s = typeof value === 'string' ? value : JSON.stringify(value);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function downloadCsv(filename, header, rows) {
  const headerLine = header.map(escapeCsvCell).join(',');
  const lines = rows.map((row) => row.map(escapeCsvCell).join(','));
  const blob = new Blob([[headerLine, ...lines].join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function FilterChipGroup({ group }) {
  const { label, options, selected, onChange, multi = true } = group;
  const has = (value) => selected.includes(value);
  const toggle = (value) => {
    if (multi) {
      onChange(has(value) ? selected.filter((v) => v !== value) : [...selected, value]);
    } else {
      onChange(has(value) ? [] : [value]);
    }
  };
  return (
    <div className="flex items-center flex-wrap gap-1.5">
      <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mr-1">
        {label}
      </span>
      {options.map((opt) => {
        const active = has(opt.value);
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => toggle(opt.value)}
            className={`text-[11px] px-2 py-0.5 rounded-full border transition-colors ${
              active
                ? 'bg-[#043132] border-[#043132] text-white'
                : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300 hover:text-slate-800'
            }`}
            title={opt.label}
          >
            {opt.label}
            {opt.count !== undefined ? (
              <span className={`ml-1 ${active ? 'text-white/70' : 'text-slate-400'}`}>{opt.count}</span>
            ) : null}
          </button>
        );
      })}
      {selected.length > 0 ? (
        <button
          type="button"
          onClick={() => onChange([])}
          className="text-[10px] text-slate-400 hover:text-slate-700 inline-flex items-center gap-0.5"
          title={`Clear ${label}`}
        >
          <X size={11} /> Clear
        </button>
      ) : null}
    </div>
  );
}

export default function AdminTable({
  rows,
  columns,
  rowKey,
  renderRow,
  searchPlaceholder = 'Search…',
  searchKeys,
  searchPredicate,
  filterGroups = [],
  defaultSort,
  defaultPageSize = 25,
  initialPageSize,
  pageSizeOptions = PAGE_SIZE_OPTIONS,
  loading = false,
  emptyMessage = 'No rows.',
  emptyMatchMessage = 'No rows match your search or filters.',
  toolbarLeft,
  toolbarRight,
  selectable = false,
  selectedKeys,
  onSelectionChange,
  csvExport,
  className = '',
  showFooter = true,
}) {
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState(defaultSort || null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize || defaultPageSize);

  const filtered = useMemo(() => {
    let out = Array.isArray(rows) ? [...rows] : [];
    const q = search.trim().toLowerCase();
    if (q) {
      if (typeof searchPredicate === 'function') {
        out = out.filter((row) => searchPredicate(row, q));
      } else if (Array.isArray(searchKeys) && searchKeys.length > 0) {
        out = out.filter((row) =>
          searchKeys.some((k) => String(row?.[k] ?? '').toLowerCase().includes(q))
        );
      }
    }
    if (sort && sort.key) {
      const col = columns.find((c) => c.key === sort.key);
      const getter = col?.sortValue || ((r) => defaultSortValue(r, sort.key));
      out.sort((a, b) => {
        const av = getter(a);
        const bv = getter(b);
        const cmp = compareValues(av, bv);
        return sort.direction === 'desc' ? -cmp : cmp;
      });
    }
    return out;
  }, [rows, search, sort, columns, searchKeys, searchPredicate]);

  const total = filtered.length;
  const isAllPage = pageSize === 'all';
  const effectiveSize = isAllPage ? Math.max(total, 1) : Number(pageSize) || defaultPageSize;
  const pageCount = Math.max(1, Math.ceil(total / effectiveSize));

  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  useEffect(() => {
    setPage(1);
  }, [search, JSON.stringify(filterGroups.map((g) => g.selected || [])), pageSize]);

  const visibleRows = useMemo(() => {
    if (isAllPage) return filtered;
    const start = (page - 1) * effectiveSize;
    return filtered.slice(start, start + effectiveSize);
  }, [filtered, page, effectiveSize, isAllPage]);

  const allKeysOnPage = useMemo(() => visibleRows.map((r) => rowKey(r)), [visibleRows, rowKey]);
  const isAllOnPageSelected = selectable && allKeysOnPage.length > 0
    && allKeysOnPage.every((k) => selectedKeys?.has?.(k) || selectedKeys?.includes?.(k));

  const handleSort = (col) => {
    if (col.sortable === false) return;
    setSort((prev) => {
      if (!prev || prev.key !== col.key) return { key: col.key, direction: 'asc' };
      if (prev.direction === 'asc') return { key: col.key, direction: 'desc' };
      return null;
    });
  };

  const handleToggleAllOnPage = () => {
    if (!onSelectionChange) return;
    const setLike = selectedKeys instanceof Set
      ? selectedKeys
      : new Set(Array.isArray(selectedKeys) ? selectedKeys : []);
    if (isAllOnPageSelected) {
      const next = new Set(setLike);
      allKeysOnPage.forEach((k) => next.delete(k));
      onSelectionChange(next);
    } else {
      const next = new Set(setLike);
      allKeysOnPage.forEach((k) => next.add(k));
      onSelectionChange(next);
    }
  };

  const handleToggleRow = (key) => {
    if (!onSelectionChange) return;
    const setLike = selectedKeys instanceof Set
      ? selectedKeys
      : new Set(Array.isArray(selectedKeys) ? selectedKeys : []);
    const next = new Set(setLike);
    if (next.has(key)) next.delete(key); else next.add(key);
    onSelectionChange(next);
  };

  const handleExportCsv = () => {
    if (!csvExport) return;
    const { filename, header, mapRow } = csvExport;
    const data = filtered.map(mapRow);
    downloadCsv(filename || 'admin-export.csv', header, data);
  };

  const showingFrom = total === 0 ? 0 : (isAllPage ? 1 : (page - 1) * effectiveSize + 1);
  const showingTo = isAllPage ? total : Math.min(page * effectiveSize, total);

  return (
    <div className={`bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden ${className}`}>
      {/* Toolbar */}
      <div className="p-4 border-b border-slate-100 bg-slate-50 space-y-3 sticky top-0 z-10">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="flex-1 min-w-0 flex items-center gap-2">
            {toolbarLeft}
            <div className="relative flex-1 min-w-0 max-w-xl">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={searchPlaceholder}
                className="w-full pl-9 pr-16 py-2 border border-slate-200 rounded-lg text-sm focus:border-[#043132] focus:ring-1 focus:ring-[#043132] outline-none bg-white"
              />
              {search ? (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  aria-label="Clear search"
                >
                  <X size={14} />
                </button>
              ) : null}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {csvExport ? (
              <button
                type="button"
                onClick={handleExportCsv}
                disabled={total === 0}
                className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:text-slate-900 hover:border-slate-300 disabled:opacity-40"
                title="Export current view as CSV"
              >
                <Download size={12} /> CSV
              </button>
            ) : null}
            {toolbarRight}
          </div>
        </div>

        {filterGroups.length > 0 ? (
          <div className="flex flex-wrap gap-x-5 gap-y-2 items-center">
            {filterGroups.map((g) => (
              <FilterChipGroup key={g.id} group={g} />
            ))}
          </div>
        ) : null}
      </div>

      {/* Body */}
      <div className="overflow-x-auto">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-500 text-sm gap-2">
            <EcointelLottieMark size={20} /> Loading…
          </div>
        ) : total === 0 ? (
          <div className="p-10 text-center text-sm text-slate-500">
            {(rows?.length || 0) === 0 ? emptyMessage : emptyMatchMessage}
          </div>
        ) : (
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-[10px] text-slate-500 uppercase font-bold border-b border-slate-100 sticky top-0">
              <tr>
                {selectable ? (
                  <th className="p-3 w-8">
                    <input
                      type="checkbox"
                      checked={isAllOnPageSelected}
                      onChange={handleToggleAllOnPage}
                      className="rounded border-slate-300 text-[#043132] focus:ring-[#043132]"
                      aria-label="Select all on this page"
                    />
                  </th>
                ) : null}
                {columns.map((col) => {
                  const sortable = col.sortable !== false;
                  const isActive = sort?.key === col.key;
                  return (
                    <th
                      key={col.key}
                      className={`p-3 ${col.align === 'right' ? 'text-right' : 'text-left'} ${col.className || ''}`}
                    >
                      {sortable ? (
                        <button
                          type="button"
                          onClick={() => handleSort(col)}
                          className={`inline-flex items-center gap-1 uppercase font-bold tracking-wider ${
                            isActive ? 'text-[#043132]' : 'text-slate-500 hover:text-slate-700'
                          }`}
                        >
                          {col.label}
                          {isActive ? (
                            sort.direction === 'asc'
                              ? <ChevronUp size={11} />
                              : <ChevronDown size={11} />
                          ) : null}
                        </button>
                      ) : (
                        col.label
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {visibleRows.map((row) => {
                const key = rowKey(row);
                const selected = selectable && (
                  selectedKeys?.has?.(key) || (Array.isArray(selectedKeys) && selectedKeys.includes(key))
                );
                return (
                  <tr key={key} className={`hover:bg-slate-50/80 align-top ${selected ? 'bg-emerald-50/40' : ''}`}>
                    {selectable ? (
                      <td className="p-3">
                        <input
                          type="checkbox"
                          checked={!!selected}
                          onChange={() => handleToggleRow(key)}
                          className="rounded border-slate-300 text-[#043132] focus:ring-[#043132]"
                          aria-label="Select row"
                        />
                      </td>
                    ) : null}
                    {renderRow(row, columns)}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Footer / pagination */}
      {showFooter && total > 0 ? (
        <div className="px-4 py-2.5 border-t border-slate-100 bg-slate-50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-xs text-slate-500">
          <div>
            Showing {showingFrom}–{showingTo} of {total}
            {(rows?.length || 0) !== total ? (
              <span className="text-slate-400"> · {rows.length} total before filtering</span>
            ) : null}
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-1.5">
              <span className="text-[10px] uppercase tracking-wider text-slate-400">Per page</span>
              <select
                value={pageSize}
                onChange={(e) => {
                  const v = e.target.value;
                  setPageSize(v === 'all' ? 'all' : Number(v));
                }}
                className="border border-slate-200 rounded px-2 py-1 text-xs bg-white"
              >
                {pageSizeOptions.map((opt) => (
                  <option key={opt} value={opt}>{opt === 'all' ? 'All' : opt}</option>
                ))}
              </select>
            </label>
            {!isAllPage ? (
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="px-2 py-1 rounded border border-slate-200 hover:border-slate-300 disabled:opacity-40"
                >
                  Prev
                </button>
                <span className="px-2">Page {page} / {pageCount}</span>
                <button
                  type="button"
                  disabled={page >= pageCount}
                  onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                  className="px-2 py-1 rounded border border-slate-200 hover:border-slate-300 disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
